/*
 * Author: Roger
 * File Created: Sunday, 24-08-04 10:40 pm
 * Last Modified: Monday, 25-03-17 03:18 pm, by Harper Yan
 */

import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { i18n, useTranslation } from 'next-i18next';
import { Box, Button, dialogClasses, Divider, drawerClasses, Link, List, ListItem, ListItemButton, ListItemText, Stack } from '@mui/material';
import { Flex } from '@ucard/ds/containers';
import type { DrawerDialogProps } from '@ucard/ds/message/dialog/templates';
import { useScreenSize } from '@ucard/ds/utils';
import { openDepointDialog } from '@/components/biz/DepointDialog';
import { useLogout } from '@/hooks/useLogout';
import { useUserInfo } from '@/stores/useUserStore';
import { LanguageSwitcher } from './LanguageSwitcher';
import { MenuItemText, StyledDialog, StyledIcon, StyledWelcomeText } from './style';
type LangProps = 'zh' | 'en';
export interface HeaderDialogProps extends DrawerDialogProps {
  isLogin: boolean;
}
const HeaderDialog: React.FC<HeaderDialogProps> = ({
  isLogin,
  onClose,
  ...rest
}) => {
  const {
    t
  } = useTranslation('common');
  const router = useRouter();
  const {
    user
  } = useUserInfo();
  const isXsScreen = useScreenSize('xs');
  const logout = useLogout();
  const [show, setShow] = useState(false);
  const currentLang = router.locale as LangProps;
  const containerRef = useRef(null);
  const goUrl = (url: string) => {
    if (url.startsWith('/')) {
      router.push(url);
      onClose!();
    } else {
      window.open(url, '_blank');
    }
  };
  const handleLogout = async () => {
    const status = await logout();
    if (status) {
      onClose!();
    }
  };
  return <StyledDialog onClose={onClose} {...rest} sx={{
    ...rest.sx,
    [`& .${dialogClasses.paper},.${drawerClasses.paper}`]: {
      overflow: 'auto'
    }
  }} data-sentry-element="StyledDialog" data-sentry-component="HeaderDialog" data-sentry-source-file="index.tsx">
      {isLogin ? <Box height="100%" position="relative">
          <Stack height="100%" pb={{
        xs: '104px',
        sm: '90px'
      }} ref={containerRef}>
            <StyledWelcomeText p="20px">
              {i18n?.t('header.welcome')}
            </StyledWelcomeText>
            <List sx={{
          flex: 1,
          overflow: 'auto'
        }}>
              <ListItemButton onClick={() => {
            goUrl('/dvault');
          }}>
                <StyledIcon fontSize="inherit" name="Wallet" />
                <ListItemText primary={i18n?.t('header.dvault')} primaryTypographyProps={{
              color: '#FFF',
              fontSize: '16px'
            }} />
                <StyledIcon fontSize="inherit" name="ArrowForward" />
              </ListItemButton>
              <ListItemButton onClick={() => {
            goUrl('/card');
          }}>
                <StyledIcon fontSize="inherit" name="Card" />
                <ListItemText primary={i18n?.t('header.card')} primaryTypographyProps={{
              color: '#FFF',
              fontSize: '16px'
            }} />
                <StyledIcon fontSize="inherit" name="ArrowForward" />
              </ListItemButton>
              <ListItemButton onClick={() => {
            goUrl('/account');
          }}>
                <StyledIcon fontSize="inherit" name="User" />
                <ListItemText primary={i18n?.t('header.account')} primaryTypographyProps={{
              color: '#FFF',
              fontSize: '16px'
            }} />
                <StyledIcon fontSize="inherit" name="ArrowForward" />
              </ListItemButton>
              {user?.pointsFunctionSwitch && <ListItemButton onClick={() => {
            goUrl('/card/depoint');
          }}>
                  <StyledIcon fontSize="inherit" name="Point" />
                  <ListItemText primary={i18n?.t('header.points')} primaryTypographyProps={{
              color: '#FFF',
              fontSize: '16px'
            }} />
                  <StyledIcon fontSize="inherit" name="ArrowForward" />
                </ListItemButton>}
              <Divider component="li" sx={{
            margin: '0 20px'
          }} />
              <ListItemButton onClick={() => {
            setShow(true);
          }}>
                <StyledIcon fontSize="inherit" name="Earth" />
                <ListItemText primary={i18n?.t('header.language')} primaryTypographyProps={{
              color: '#FFF',
              fontSize: '16px'
            }} />
                <MenuItemText>
                  {currentLang === 'en' ? 'English' : '简体中文'}
                </MenuItemText>
                <StyledIcon fontSize="inherit" name="ArrowForward" />
              </ListItemButton>
              <ListItemButton onClick={() => {
            goUrl(currentLang === 'en' ? 'https://help.thedecard.com/v/en/decard' : 'https://help.thedecard.com/decard');
          }}>
                <StyledIcon fontSize="inherit" name="Help" />
                <ListItemText primary={i18n?.t('header.help')} primaryTypographyProps={{
              color: '#FFF',
              fontSize: '16px'
            }} />
                <StyledIcon fontSize="inherit" name="ArrowForward" />
              </ListItemButton>
              <ListItem>
                <StyledIcon fontSize="inherit" name="Contact" />
                <ListItemText primary={i18n?.t('header.contact')} primaryTypographyProps={{
              color: '#FFF',
              fontSize: '16px'
            }} />
                <MenuItemText>
                  <Link color="inherit" href="https://t.me/TheDeHelpBot" target="_blank" underline="none">
                    @DeHelpBot
                  </Link>
                </MenuItemText>
              </ListItem>
            </List>
            <LanguageSwitcher containerRef={containerRef.current} isXsScreen={isXsScreen} onClose={() => {
          setShow(false);
        }} open={show} />
          </Stack>
          <Flex p="20px" sx={{
        position: 'absolute',
        bottom: 0,
        width: '100%',
        pb: ['54px', '20px']
      }}>
            <Button fullWidth onClick={handleLogout} sx={{
          color: 'white',
          borderColor: '#4F5867'
        }} variant="outlined">
              {i18n?.t('header.logout')}
            </Button>
          </Flex>
        </Box> : <Stack overflow="hidden" position="relative" ref={containerRef} width="100%">
          <StyledWelcomeText pb="20px" pt={3} px="20px">
            {i18n?.t('header.welcome')}
          </StyledWelcomeText>
          <Stack direction="row" gap="15px" px="20px" py="16px" width="100%">
            <Button onClick={() => {
          goUrl('/login');
        }} sx={{
          flexGrow: 1,
          padding: '10px 12px',
          lineHeight: '26px',
          border: '1px solid #4F5867',
          color: '#FFF'
        }} variant="outlined">
              {i18n?.t('header.login')}
            </Button>
            <Button onClick={() => {
          goUrl('/signup');
        }} sx={{
          flexGrow: 1,
          padding: '10px 12px',
          lineHeight: '26px'
        }}>
              {i18n?.t('header.register')}
            </Button>
          </Stack>
          <List sx={{
        marginBottom: ['54px', '20px']
      }}>
            <ListItemButton onClick={() => {
          setShow(true);
        }}>
              <StyledIcon fontSize="inherit" name="Earth" />
              <ListItemText primary={i18n?.t('header.language')} primaryTypographyProps={{
            color: '#FFF',
            fontSize: '16px'
          }} />
              <MenuItemText sx={{
            color: '#707A8A',
            marginRight: '4px'
          }}>
                {currentLang === 'en' ? 'English' : '简体中文'}
              </MenuItemText>
              <StyledIcon fontSize="inherit" name="ArrowForward" />
            </ListItemButton>
            <ListItem>
              <StyledIcon fontSize="inherit" name="Contact" />
              <ListItemText primary={i18n?.t('header.contact')} primaryTypographyProps={{
            color: '#FFF',
            fontSize: '16px'
          }} />
              <MenuItemText>
                <Link color="inherit" href="https://t.me/TheDeHelpBot" target="_blank" underline="none">
                  @DeHelpBot
                </Link>
              </MenuItemText>
            </ListItem>
          </List>
          <LanguageSwitcher containerRef={containerRef.current} isXsScreen={isXsScreen} onClose={() => {
        setShow(false);
      }} open={show} />
        </Stack>}
    </StyledDialog>;
};
export default HeaderDialog;